import { Menu, ActionIcon } from '@mantine/core';
import { IconDotsVertical } from '@tabler/icons-react';
import type { Folder } from '../../../schemas/folder.schema';
import { getDriveFolderActions } from '../../registry/defineDriveActions';
import {
  canChangeFolderAccess,
  canModifyFolder,
  canWriteFolder,
  getEffectiveAccess,
} from './driveAccess';
import { useFolderArchiveDownload } from './useFolderArchiveDownload';

interface FolderActionsMenuProps {
  folder: Folder;
  /** Все папки Диска — для вычисления эффективных прав (корни зон). */
  foldersById: Map<number, Folder>;
  currentUserId: number | null;
  isAdmin: boolean;
  onCreateChild: (parentFolderId: number) => void;
  onRename: (folder: Folder) => void;
  onMove: (folder: Folder) => void;
  onDelete: (folder: Folder) => void;
  onChangeAccess: (folder: Folder) => void;
  /** Перечитать Диск — нужен доменным пунктам из `defineDriveActions`. */
  onRefresh: () => void;
}

/**
 * FolderActionsMenu — меню действий папки (узел дерева, строка таблицы Диска).
 * «Скачать архивом» — всем, кто видит папку (ZIP собирается фоном, сервер
 * проверяет видимость зоны сам); «Новая папка» — по праву записи в зону;
 * управление — по матрице зоны (public_write — автор, public_read/private —
 * владелец зоны, ADMIN — всё); у системных папок управление скрыто.
 * Сервер authoritative.
 *
 * Ниже штатных пунктов — доменные, зарегистрированные модулями через
 * `defineDriveActions` для папок с подходящим якорем. Их доступность платформа
 * не считает: кому можно «Привязать к NCR», знает только сам модуль.
 */
export function FolderActionsMenu({
  folder,
  foldersById,
  currentUserId,
  isAdmin,
  onCreateChild,
  onRename,
  onMove,
  onDelete,
  onChangeAccess,
  onRefresh,
}: FolderActionsMenuProps) {
  const effective = getEffectiveAccess(folder, foldersById);
  const canManage = canModifyFolder(folder, effective, currentUserId, isAdmin);
  const canCreateInside = canWriteFolder(effective, currentUserId, isAdmin);
  // «Доступ…» — отдельный гейт: в custom-зоне это администрирование (admin+),
  // а у не-корня внутри custom явные режимы запрещены листовостью
  const canAccess = canChangeFolderAccess(folder, effective, currentUserId, isAdmin);
  const { downloadingFolderId, downloadFolderArchive } = useFolderArchiveDownload();
  const domainActions = getDriveFolderActions(folder);

  return (
    <Menu position='bottom-end' withArrow shadow='md'>
      <Menu.Target>
        <ActionIcon
          size='sm'
          variant='subtle'
          color='gray'
          onClick={e => e.stopPropagation()}
          title='Действия'
        >
          <IconDotsVertical size={14} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          disabled={downloadingFolderId === folder.id}
          onClick={e => {
            e.stopPropagation();
            downloadFolderArchive(folder);
          }}
        >
          Скачать архивом
        </Menu.Item>
        <Menu.Item
          disabled={!canCreateInside}
          onClick={e => {
            e.stopPropagation();
            onCreateChild(folder.id);
          }}
        >
          Новая папка
        </Menu.Item>
        {!folder.is_system && (
          <>
            <Menu.Item
              disabled={!canManage}
              onClick={e => {
                e.stopPropagation();
                onRename(folder);
              }}
            >
              Переименовать
            </Menu.Item>
            <Menu.Item
              disabled={!canManage}
              onClick={e => {
                e.stopPropagation();
                onMove(folder);
              }}
            >
              Переместить
            </Menu.Item>
            <Menu.Item
              disabled={!canAccess}
              onClick={e => {
                e.stopPropagation();
                onChangeAccess(folder);
              }}
            >
              Доступ…
            </Menu.Item>
            <Menu.Item
              color='red'
              disabled={!canManage}
              onClick={e => {
                e.stopPropagation();
                onDelete(folder);
              }}
            >
              Удалить
            </Menu.Item>
          </>
        )}

        {/* Доменные пункты модулей — отдельной группой, чтобы не смешивались
            со штатными операциями над папкой */}
        {domainActions.length > 0 && (
          <>
            <Menu.Divider />
            {domainActions.map(action => (
              <Menu.Item
                key={action.title}
                color={action.color}
                onClick={e => {
                  e.stopPropagation();
                  void action.handler({ folder, refresh: onRefresh });
                }}
              >
                {action.title}
              </Menu.Item>
            ))}
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
